import { Field, GraphConfiguration, Model } from "@/type/common";
import {
  endOfDay,
  endOfMonth,
  endOfQuarter,
  endOfWeek,
  endOfYear,
  startOfDay,
  startOfMonth,
  startOfQuarter,
  startOfWeek,
  startOfYear,
  sub,
  format,
} from "date-fns";

export const refactor_models = (models: Model[]) => {
  return models.map((model) => ({
    model: model.model,
    name: model.name,
    label: model.name,
    value: model.model,
    search: `${model.model} ${model.name}`,
  }));
};

export const refactor_fields = (fields: Field[]) => {
  return fields.map((field) => ({
    field: field.field,
    name: field.name,
    type: field.type,
    selection: field.selection,
    label: field.name,
    value: field.field,
    search: `${field.field} ${field.name}`,
    model: field.model,
  }));
};

const domainRuleMap = (value: any) => {
  const now = new Date();
  const map: any = {
    "@@now": now,
    "@@start_of_today": startOfDay(now),
    "@@end_of_today": endOfDay(now),
    "@@start_of_week": startOfWeek(now),
    "@@end_of_week": endOfWeek(now),
    "@@start_of_month": startOfMonth(now),
    "@@end_of_month": endOfMonth(now),
    "@@start_of_year": startOfYear(now),
    "@@end_of_year": endOfYear(now),
    "@@start_of_quarter": startOfQuarter(now),
    "@@end_of_quarter": endOfQuarter(now),
    "@@today": format(startOfDay(now), "yyyy-MM-dd"),
    "@@yesterday": format(startOfDay(sub(now, { days: 1 })), "yyyy-MM-dd"),
    "@@first_of_week": format(startOfWeek(now), "yyyy-MM-dd"),
    "@@last_of_week": format(endOfWeek(now), "yyyy-MM-dd"),
    "@@first_of_month": format(startOfMonth(now), "yyyy-MM-dd"),
    "@@last_of_month": format(endOfMonth(now), "yyyy-MM-dd"),
    "@@first_of_year": format(startOfYear(now), "yyyy-MM-dd"),
    "@@last_of_year": format(endOfYear(now), "yyyy-MM-dd"),
    "@@first_of_quarter": format(startOfQuarter(now), "yyyy-MM-dd"),
    "@@last_of_quarter": format(endOfQuarter(now), "yyyy-MM-dd"),
    "@@me": parseInt(localStorage.getItem("user_id") || "0"),
    "@@my_partner": parseInt(localStorage.getItem("partner_id") || "0"),
  };
  return map[value];
};

const domainSpecialRuleUpdate = (field: string, special: string) => {
  const map: any = {
    "!!today": [
      [field, ">=", startOfDay(new Date())],
      [field, "<=", endOfDay(new Date())],
    ],
    "!!this_week": [
      [field, ">=", startOfWeek(new Date())],
      [field, "<=", endOfWeek(new Date())],
    ],
    "!!this_month": [
      [field, ">=", startOfMonth(new Date())],
      [field, "<=", endOfMonth(new Date())],
    ],
    "!!this_quarter": [
      [field, ">=", startOfQuarter(new Date())],
      [field, "<=", endOfQuarter(new Date())],
    ],
    "!!this_year": [
      [field, ">=", startOfYear(new Date())],
      [field, "<=", endOfYear(new Date())],
    ],
  };

  return map[special];
};

export const cleanDomain = (domain: any[]) => {
  if (!domain || domain.length === 0) return [];
  const updatedDomain = domain.map((rule) => {
    if (
      rule.length === 3 &&
      typeof rule[2] === "string" &&
      rule[2]?.startsWith("@@")
    ) {
      return [rule[0], rule[1], domainRuleMap(rule[2])];
    }
    if (rule.length === 3 && Array.isArray(rule[2])) {
      return [
        rule[0],
        rule[1],
        rule[2].map((value) => {
          if (typeof value === "string" && value?.startsWith("@@")) {
            return domainRuleMap(value);
          }
          return value;
        }),
      ];
    }
    return rule;
  });
  const fullDomain: any[] = [];
  updatedDomain.forEach((rule) => {
    if (
      rule &&
      rule.length === 3 &&
      rule[1] === "=" &&
      typeof rule[2] === "string" &&
      rule[2]?.startsWith("!!")
    ) {
      fullDomain.push(...domainSpecialRuleUpdate(rule[0], rule[2]));
    } else {
      fullDomain.push(rule);
    }
  });
  return fullDomain.filter((rule) => rule !== undefined);
};

// Deep clone function that preserves undefined values
export const deepClone = (obj: any): any => {
  if (obj === null || typeof obj !== "object") return obj;
  if (obj instanceof Date) return new Date(obj.getTime());
  if (Array.isArray(obj)) return obj.map(deepClone);

  const cloned: any = {};
  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      cloned[key] = deepClone(obj[key]);
    }
  }
  return cloned;
};

export const refactor_config = (
  oldConfig: GraphConfiguration,
  is_preview = false
) => {
  const config: any = deepClone(oldConfig);
  config.data_source.domain = cleanDomain(config.data_source.domain);
  if (is_preview) {
    config.data_source.preview = true;
  }
  return config;
};
